import {
  Bell,
  CalendarClock,
  CreditCard,
  FileText,
  MessageSquare,
  Pill,
  Salad,
} from "lucide-react";

/**
 * Iconography for nudge categories — and nothing else.
 *
 * The category taxonomy itself (which categories exist, what they are called,
 * which reach a console user) is **owned by the backend registry** and fetched
 * at runtime. Hardcoding it here would mean a Console release every time a
 * category is added, which is exactly what the registry exists to avoid.
 *
 * What stays here is the mapping from a semantic icon hint to a real icon,
 * because a server has no business shipping lucide component names. The
 * backend sends `icon: "chat"`; this decides what "chat" looks like. The
 * vocabulary is fixed and agreed with the backend — chat, appointment,
 * prescription, meal_plan, billing, document, bell — and deliberately not
 * extended unilaterally.
 *
 * An unknown hint — or none at all — falls back to a neutral bell, so a
 * brand-new category renders sensibly on a Console build that predates it.
 */
export const NUDGE_ICONS: Record<string, typeof Bell> = {
  chat: MessageSquare,
  appointment: CalendarClock,
  prescription: Pill,
  meal_plan: Salad,
  billing: CreditCard,
  document: FileText,
  bell: Bell,
};

export const getNudgeIcon = (hint?: string): typeof Bell =>
  (hint && NUDGE_ICONS[hint]) || Bell;

/**
 * Turns a category key into something readable when the backend has not given
 * us a label — an older Console build meeting a newer category.
 * `invoice_overdue` becomes `Invoice overdue`.
 */
export const humaniseCategory = (key: string) =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/^\s*\w/, (c) => c.toUpperCase())
    .trim();
